import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { 
  Box, TextField, Button, MenuItem, Select, InputLabel, 
  FormControl, List, ListItem, ListItemText, ListItemSecondaryAction, 
  IconButton, Divider, Typography, Paper
} from '@material-ui/core'; 
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit'; // 👈 NEU: Stift-Icon importiert
import axios from 'axios';

export default function SettingTypesMgntCard({typeOfSetting, textForHeader, props}) {
  const { t } = useTranslation();

  const [newOption, setNewOption] = useState({
    language: 'de',
    type: typeOfSetting,
    key: '',   
    value: ''  
  });

  const [existingOptions, setExistingOptions] = useState([]);
  const [statusMsg, setStatusMsg] = useState("");
  
  // 👈 NEU: Zustand für den aktuell bearbeiteten Eintrag
  const [editingId, setEditingId] = useState(null);
  
  // 1. Funktion mit useCallback definieren, damit sie überall verfügbar ist
  const loadOptions = React.useCallback(() => {
    axios.get(`/settings/options?type=${typeOfSetting}&lang=${newOption.language}`)
      .then(res => {
        if (res.data) setExistingOptions(res.data);
      })
      .catch(err => console.error("Fehler beim Laden:", err));
  }, [newOption.language, typeOfSetting]); // Aktualisiert sich automatisch, wenn sich die Sprache ändert

  // 2. Der useEffect ruft sie nun einfach beim Sprachwechsel auf
  useEffect(() => {
    loadOptions();
  }, [loadOptions]); // loadOptions ist hier als sichere Abhängigkeit registriert

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewOption(prev => ({ ...prev, [name]: value }));
  };

  // 👈 NEU: Funktion, die beim Klick auf den Stift die Daten in die Inputs lädt
  const handleEditClick = (option) => {
    setEditingId(option.id);
    setNewOption({
      language: option.language,
      type: option.type,
      key: option.key,
      value: option.value
    });
  };

  // 👈 NEU: Setzt das Formular wieder in den Erstell-Modus zurück
  const handleCancelEdit = () => {
    setEditingId(null);
    setNewOption(prev => ({ ...prev, key: '', value: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newOption.key.trim() || !newOption.value.trim()) {
      setStatusMsg(t("SettingTypesMgntCard.statusMsg_missing_keyvalue"));
      return;
    }

    if (editingId) {
      // 👈 NEU: Wenn eine ID existiert, senden wir einen PUT-Request zum Ändern
      axios.put(`/settings/options/${editingId}`, newOption)
        .then(() => {
          setStatusMsg(t("SettingTypesMgntCard.statusMsg_edit_cat"));
          setEditingId(null);
          setNewOption(prev => ({ ...prev, key: '', value: '' }));
          loadOptions();
          setTimeout(() => setStatusMsg(""), 3000);
        })
        .catch(() => setStatusMsg(t("SettingTypesMgntCard.statusMsg_error_edit")));
    } else {
      // Normaler POST-Request für neue Einträge
      axios.post('/settings/options', newOption)
        .then(() => {
          setStatusMsg(t("SettingTypesMgntCard.statusMsg_add_cat"));
          setNewOption(prev => ({ ...prev, key: '', value: '' }));
          loadOptions();
          setTimeout(() => setStatusMsg(""), 3000);
        })
        .catch(() => setStatusMsg(t("SettingTypesMgntCard.statusMsg_error_add")));
    }
  };

  const handleDelete = (id) => {
    if (editingId === id) handleCancelEdit(); // Falls der gelöschte Eintrag gerade editiert wurde

    axios.delete(`/settings/options/${id}`)
      .then(() => {
        loadOptions();
      })
      .catch(() => setStatusMsg(t("SettingTypesMgntCard.statusMsg_error_delete")));
  };

  return (
    <div className="doc_box" style={{ width: "100%", maxWidth: "800px", margin: "20px auto", padding: "20px" }}>
      <Typography variant="h6" style={{ fontWeight: "bold", marginBottom: "15px" }}>
			{t("SettingTypesMgntCard.pageTitle", {value: textForHeader })} {editingId && t("SettingTypesMgntCard.editMode")}
      </Typography>

      {statusMsg && <div style={{ color: "#4f40b3", marginBottom: "10px", fontWeight: "bold" }}>{statusMsg}</div>}

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <div style={{ display: "flex", gap: "15px" }}>
          <FormControl variant="outlined" style={{ flex: 1 }} disabled={!!editingId}> 
            <InputLabel id="lang-select-label">{t("user.language")}</InputLabel>
            <Select
              labelId="lang-select-label"
              name="language"
              value={newOption.language}
              onChange={handleInputChange}
              label={t("user.language")}
            >
              <MenuItem value="de">Deutsch (de)</MenuItem>
              <MenuItem value="en">English (en)</MenuItem>
            </Select>
          </FormControl>

          <Box 
            style={{ 
              flex: 1, 
              display: "flex", 
              flexDirection: "column", 
              justifyContent: "center",
              backgroundColor: "#f5f5f5", 
              borderRadius: "4px", 
              padding: "0 14px",
              height: "56px", 
              border: "1px solid rgba(0, 0, 0, 0.12)"
            }}
          >
            <Typography variant="caption" style={{ color: "rgba(0, 0, 0, 0.54)", fontSize: "12px", textAlign: "left" }}>
              Type
            </Typography>
            <Typography variant="body1" style={{ color: "rgba(0, 0, 0, 0.38)", fontWeight: 500, textAlign: "left" }}>
              {typeOfSetting}
            </Typography>
          </Box>
        </div>

        <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
          <TextField
			label={t("SettingTypesMgntCard.key")}
            name="key"
            value={newOption.key}
            onChange={handleInputChange}
            variant="outlined"
            style={{ flex: 1 }}
          />
          <TextField
			label={t("SettingTypesMgntCard.value")}
            name="value"
            value={newOption.value}
            onChange={handleInputChange}
            variant="outlined"
            style={{ flex: 1 }}
          />
          
          {/* 👈 Dynamischer Button-Text und Abbrechen-Option */}
          <Button type="submit" variant="contained" color={editingId ? "secondary" : "primary"} style={{ height: "56px", flexShrink: 0 }}>
            {editingId ? t("common.save") : t("common.add")}
          </Button>
          
          {editingId && (
            <Button variant="outlined" onClick={handleCancelEdit} style={{ height: "56px", flexShrink: 0 }}>
			  {t("common.cancel")}
            </Button>
          )}
        </div>
      </form>

      <Divider style={{ margin: "20px 0" }} />

      <Typography variant="subtitle2" style={{ fontWeight: "bold", marginBottom: "10px" }}>
        Hinterlegte Werte für ({newOption.language.toUpperCase()}):
      </Typography>

      <Paper variant="outlined" style={{ maxHeight: "250px", overflowY: "auto" }}>
        <List dense>
          {existingOptions.length > 0 ? (
            existingOptions.map((option) => (
              <ListItem key={option.id} divider style={{ backgroundColor: editingId === option.id ? "#eae6ff" : "transparent" }}>
                <ListItemText 
                  primary={<strong>{option.key ||  t("SettingTypesMgntCard.missing_key") }</strong>} 
                  secondary={`Anzeige: ${option.value}`} 
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end" onClick={() => handleEditClick(option)} style={{ color: "#4f40b3", marginRight: "8px" }}>
                    <EditIcon />
                  </IconButton>
                  <IconButton edge="end" onClick={() => handleDelete(option.id)} style={{ color: "tomato" }}>
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))
          ) : (
            <ListItem>
				<ListItemText primary={t("common.no_values")} style={{ fontStyle: "italic", color: "gray" }} />
            </ListItem>
          )}
        </List>
      </Paper>
    </div>
  );
}
